import type { Faq as FaqItem } from 'libs/http/api/content/content.types';

import { CreditGaugeCard } from 'ui/molecules/CreditGaugeCard/CreditGaugeCard';
import { FinancialStatementCard } from 'ui/molecules/FinancialStatementCard/FinancialStatementCard';
import { OwnershipGraphCard } from 'ui/molecules/OwnershipGraphCard/OwnershipGraphCard';
import { PerpetualFeedCard } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard';
import type { FeedAlert } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard.types';
import { RegistryRecordCard } from 'ui/molecules/RegistryRecordCard/RegistryRecordCard';
import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import type { DifferenceItem } from 'ui/organisms/DifferenceSection/DifferenceSection.types';
import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';

import styles from './FinancialServicesPage.module.scss';

type Translate = (key: string) => string;

export const getAccessRoutes = (t: Translate): AccessRoute[] => [
  {
    name: 'Platform',
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t('Query any company by registry ID — documented endpoints, JSON responses, per-field provenance.'),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool.'),
    actions: [{ label: t('MCP server'), href: '/mcp-integration' }],
  },
  {
    name: 'Regis AI',
    desc: t('Ask questions in natural language across the dataset and get sourced answers.'),
    actions: [{ label: t('Meet Regis AI'), href: '/regis' }],
  },
];

export const getFaqItems = (t: Translate): FaqItem[] => [
  {
    id: 'source',
    default_opened: true,
    question: t('Where does the data actually come from?'),
    answer: t(
      'Directly from official government registries — <b>400+ of them across 200+ countries</b>. Each field carries the source registry, a link to the underlying filing, and the timestamp it was retrieved.',
    ),
  },
  {
    id: 'defend',
    question: t('Can we defend a decision to a regulator?'),
    answer: t(
      'Yes. Every value points back to a filing, so an onboarding, screening, or credit decision can be reconstructed from source rather than from a model no one outside the vendor can see.',
    ),
  },
  {
    id: 'identifier',
    question: t('Do we get locked into a proprietary identifier?'),
    answer: t(
      'No. We use the <b>open registry numbers</b> your teams already recognise — Companies House numbers, EIN, and their equivalents worldwide. There is no DUNS-style number to license.',
    ),
  },
  {
    id: 'estimated',
    question: t('How do you handle estimated figures?'),
    answer: t(
      'Modelled figures such as estimated revenue and headcount are <b>labelled modelled</b> and kept separate from filed fact, so credit and risk teams always know which is which.',
    ),
  },
  {
    id: 'redistribute',
    question: t('Can we redistribute the data?'),
    answer: t(
      'Reseller and redistribution rights are available, which lets bureaus and data providers enrich their own product with primary records they can attribute to source.',
    ),
  },
  {
    id: 'current',
    question: t('How current is the data?'),
    answer: t(
      'Company records are <b>synced daily</b> from the registries, and change monitoring surfaces filing, status, and ownership updates across your portfolio as the underlying record moves. A modelled estimate stays labelled modelled rather than presented as a fresh filing.',
    ),
  },
  {
    id: 'ubo',
    question: t('How do you determine ultimate beneficial owners?'),
    answer: t(
      'We follow the ownership and control chain through registry filings — share allotments, PSC and equivalent beneficial-ownership registers — to the individuals behind the entity. <b>Each link is attributed to the filing that establishes it</b>, and percentages are shown where they are filed.',
    ),
  },
  {
    id: 'screening',
    question: t('Can you screen against sanctions and PEP lists?'),
    answer: t(
      'Yes. We screen the entity and the people attached to it — <b>directors, shareholders and UBOs</b> — against sanctions and PEP lists, at onboarding and continuously. Because parties are resolved against the registry record, a hit is traceable to the exact party it belongs to.',
    ),
  },
  {
    id: 'financials',
    question: t('Do you cover private company financials, or only listed companies?'),
    answer: t(
      'Both. We hold <b>filed financial statements for private and listed companies</b>, with up to two decades of history where the registry keeps it. Original filed documents are included, and any modelled estimate such as estimated revenue is labelled modelled.',
    ),
  },
  {
    id: 'security',
    question: t('How is data protection handled, and where is data processed?'),
    answer: t(
      'The records are company-register data drawn from official public filings. We share our data-processing and security documentation — including processing locations and applicable data-protection terms — with your procurement and security teams as part of evaluation.',
    ),
  },
];

export const getChallengerItems = (t: Translate): NumberedFeatureCard[] => [
  {
    num: '01',
    title: t('Sourced from the registry'),
    description: t(
      'Every field comes straight from the government registry that issued it — never a third party’s copy.',
    ),
  },
  {
    num: '02',
    title: t('Provenance on every field'),
    description: t(
      'Each value is linked to the exact filing it was drawn from, so any figure can be traced back to source.',
    ),
  },
  {
    num: '03',
    title: t('Timestamped on retrieval'),
    description: t('The moment each field was retrieved is recorded, so its freshness is never left in question.'),
  },
];

const FINANCIALS_TABLE = {
  columns: ['GBP', 'FY2023', 'FY2024', 'FY2025'],
  rows: [
    { label: 'Turnover', values: ['38.2M', '41.6M', '44.9M'] },
    { label: 'Profit after tax', values: ['4.1M', '4.8M', '5.3M'], up: true },
    { label: 'Total assets', values: ['61.5M', '65.2M', '69.8M'] },
    { label: 'Net assets', values: ['26.9M', '29.1M', '31.7M'], total: true },
  ],
};

const MONITORING_ALERTS: FeedAlert[] = [
  {
    company: 'Operating Ltd',
    registry: 'Companies House (UK)',
    change: 'New PSC filed',
    variant: 'ownership',
    time: '07:18',
    filing: 'Persons with significant control — new entry',
    from: 'R. Santos · 25%+ shares',
    to: 'R. Santos · 25%+ shares · Kestrel Nominees Ltd added',
    reference: 'Company 08804411 · filed 08 Jul 2026',
  },
  {
    company: 'Holdco S.A.',
    registry: 'RCS Luxembourg',
    change: 'Director appointed',
    variant: 'director',
    time: '08:02',
    filing: 'Appointment of manager',
    from: '2 managers registered',
    to: '3 managers registered',
    reference: 'B 198765 · filed 08 Jul 2026',
  },
  {
    company: 'Midco B.V.',
    registry: 'KVK (NL)',
    change: 'Share allotment filed',
    variant: 'ownership',
    time: '08:41',
    filing: 'Capital increase — new shares issued',
    from: '€1.2M issued capital',
    to: '€1.6M issued capital',
    reference: 'KVK 34567890 · filed 08 Jul 2026',
  },
  {
    company: 'Operating Ltd',
    registry: 'Companies House (UK)',
    change: 'Registered office changed',
    variant: 'address',
    time: '09:12',
    filing: 'Change of registered office address',
    from: '14 Fenwick Road, London',
    to: '2 Meridian Court, London',
    reference: 'Company 08804411 · filed 08 Jul 2026',
  },
];

export const getLifecycleItems = (t: Translate): DifferenceItem[] => [
  {
    number: '01',
    title: t('Onboard'),
    description: (
      <>
        {t('Registration number, legal form, status and registered office — straight from')}{' '}
        <b>{t('the registry that issued them')}</b>
        {t(', not a third party’s copy.')}
      </>
    ),
    media: <RegistryRecordCard className={styles.lifecycleMedia} />,
  },
  {
    number: '02',
    title: t('Map ownership'),
    description: (
      <>
        {t('The control chain to the people who actually own it, across borders and holding layers —')}{' '}
        <b>{t('every hop tied to its filing')}</b>.
      </>
    ),
    media: <OwnershipGraphCard className={styles.lifecycleMedia} />,
  },
  {
    number: '03',
    title: t('Financial intelligence'),
    description: (
      <>
        {t('Revenue, profit, assets and filing history —')} <b>{t('extracted straight from the accounts')}</b>
        {t(', with multi-year trends ready to read.')}
      </>
    ),
    media: (
      <FinancialStatementCard
        className={styles.lifecycleMedia}
        companyName="Operating Ltd"
        companyMeta="Filed accounts · Companies House"
        badge="Filed accounts"
        source="Companies House — filed accounts, retrieved per period"
        table={FINANCIALS_TABLE}
      />
    ),
  },
  {
    number: '04',
    title: t('Assess'),
    description: (
      <>
        {t('A financial-health score built')} <b>{t('on the filed accounts')}</b>
        {t(', with modelled estimates flagged and kept separate from filed fact.')}
      </>
    ),
    media: <CreditGaugeCard className={styles.lifecycleMedia} />,
  },
  {
    number: '05',
    title: t('Monitor'),
    description: (
      <>
        {t('When a director, owner or status changes,')} <b>{t('the file updates and your team is alerted')}</b>{' '}
        {t('— not at the next annual review.')}
      </>
    ),
    media: (
      <PerpetualFeedCard
        className={styles.lifecycleMedia}
        alerts={MONITORING_ALERTS}
        monitoredCount="6"
        footerNote="Counterparty group · 6 registries"
      />
    ),
  },
];
